import { Link } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  Bars3Icon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useLogout } from '@/api/hooks/useAuth';
import { clsx } from 'clsx';
import { NotificationBell } from '@/components/notifications';

export function Header() {
  const { toggleSidebar } = useUIStore();
  const { user } = useAuthStore();
  const logoutMutation = useLogout();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-line">
      <div className="flex items-center justify-between h-16 px-6">
      {/* Right side (first in DOM = right in RTL): hamburger + notifications + profile */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="w-10 h-10 rounded-[11px] border border-line flex items-center justify-center hover:bg-app transition-colors lg:hidden"
          aria-label="פתח/סגור תפריט צד"
        >
          <Bars3Icon className="w-[18px] h-[18px] text-ink-soft" />
        </button>

        <NotificationBell />

        <Menu as="div" className="relative">
          <Menu.Button
            className="flex items-center gap-2.5 ps-1 pe-2 py-1 rounded-lg hover:bg-app transition-colors"
            aria-label="תפריט משתמש"
          >
            <div className="w-[38px] h-[38px] rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-[13px] bg-gradient-to-br from-primary-600 to-primary-500">
              {user?.fullName?.trim()?.[0] || <UserCircleIcon className="w-5 h-5" />}
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-[13px] font-bold leading-tight text-ink">{user?.fullName}</div>
              <div className="text-[11px] leading-tight text-ink-soft mt-0.5">{user?.role}</div>
            </div>
            {/* Signals this is a dropdown (settings/profile/logout) - without it, nothing about
                the name/avatar looked clickable, so users never discovered the menu existed. */}
            <ChevronDownIcon className="hidden sm:block w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          </Menu.Button>

          {/* Headless UI v2: Menu.Items has its own built-in `transition` prop now - wrapping it
              in a separate v1-style <Transition as={Fragment}> (the old API) crashed with
              "Did you forget to passthrough the ref to the actual DOM node?" the moment a
              Menu.Item's Link navigated away while the panel was still closing, caught by the
              app's ErrorBoundary and shown as a full error page. */}
          <Menu.Items
            transition
            className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/profile"
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2 text-sm',
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    )}
                  >
                    <UserCircleIcon className="w-5 h-5" />
                    הפרופיל שלי
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/settings"
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2 text-sm',
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    )}
                  >
                    <Cog6ToothIcon className="w-5 h-5" />
                    הגדרות
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={clsx(
                      'flex items-center gap-2 w-full px-4 py-2 text-sm',
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    )}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    התנתקות
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>

      {/* Search removed for now - it wasn't wired to any real search logic (no onChange/query),
          which is worse than not having it. Re-add once a real global search exists. */}
      </div>
    </header>
  );
}
